import { FC } from 'react';
import {
  classificaDensita,
  densitaDi,
  MATCH_REF_DENSITY,
  TB_EFFECTIVE_MAX,
  TB_EFFECTIVE_MIN,
  TB_FIELD_LENGTH_MAX,
  TB_FIELD_LENGTH_MIN,
  TB_FIELD_WIDTH_MAX,
  TB_FIELD_WIDTH_MIN,
  TB_PLAYERS_MAX,
  TB_PLAYERS_MIN,
} from '@leopart/calc';
import CustomTextInput from '../../../shared/CustomInput/CustomTextInput';
import DensityScale from '../shared/DensityScale';
import { IBlockFormValues } from './useBlockForm';
import styles from './GameContextFields.module.scss';

interface IGameContextFieldsProps {
  values: IBlockFormValues;
  setField: <K extends keyof IBlockFormValues>(
    key: K,
    value: IBlockFormValues[K],
  ) => void;
}

/** Input numerico vuoto → `null`, mai NaN nel payload. */
const toNullable = (v: number): number | null => (Number.isNaN(v) ? null : v);

/**
 * Sezione «Contesto di gioco» del form blocco (ADR 029): dimensioni campo,
 * giocatori e tempo effettivo, con la densità m²/giocatore calcolata LIVE
 * mentre si compila — mai persistita, sempre derivata da @leopart/calc.
 * Visibile solo se una categoria del blocco abilita il gruppo.
 */
const GameContextFields: FC<IGameContextFieldsProps> = ({ values, setField }) => {
  const densita = densitaDi(
    values.fieldWidthM,
    values.fieldLengthM,
    values.playersOnField,
  );
  const range = densita == null ? null : classificaDensita(densita);
  const effettivoOltreDurata =
    values.effectiveMinutes != null &&
    values.effectiveMinutes > values.durationMinutes;

  return (
    <div className={styles.section}>
      <div className={styles.head}>
        Contesto di gioco <span className={styles.tag}>Facoltativo</span>
      </div>

      <div className={styles.row}>
        <CustomTextInput
          type="number"
          label="Campo — larghezza (m)"
          min={TB_FIELD_WIDTH_MIN}
          max={TB_FIELD_WIDTH_MAX}
          placeholder="30"
          value={values.fieldWidthM ?? ''}
          onValueNumberChange={(v) =>
            setField('fieldWidthM', toNullable(v.valueAsNumber))
          }
        />
        <CustomTextInput
          type="number"
          label="Campo — lunghezza (m)"
          min={TB_FIELD_LENGTH_MIN}
          max={TB_FIELD_LENGTH_MAX}
          placeholder="40"
          value={values.fieldLengthM ?? ''}
          onValueNumberChange={(v) =>
            setField('fieldLengthM', toNullable(v.valueAsNumber))
          }
        />
      </div>

      <div className={styles.row}>
        <CustomTextInput
          type="number"
          label="N. giocatori (tot. in campo)"
          min={TB_PLAYERS_MIN}
          max={TB_PLAYERS_MAX}
          placeholder="10"
          value={values.playersOnField ?? ''}
          onValueNumberChange={(v) =>
            setField('playersOnField', toNullable(v.valueAsNumber))
          }
        />
        <CustomTextInput
          type="number"
          label="Tempo effettivo (min)"
          min={TB_EFFECTIVE_MIN}
          max={TB_EFFECTIVE_MAX}
          placeholder="12"
          value={values.effectiveMinutes ?? ''}
          onValueNumberChange={(v) =>
            setField('effectiveMinutes', toNullable(v.valueAsNumber))
          }
        />
      </div>

      <div className={styles.preview}>
        {densita == null ? (
          <span className={styles.previewHint}>
            Densità: inserisci dimensioni campo e n. giocatori per calcolare i
            m²/giocatore (rif. gara ~{MATCH_REF_DENSITY}).
          </span>
        ) : (
          <>
            <div className={styles.previewValueRow}>
              <span className={styles.previewValue}>
                {Math.round(densita)}
                <span className={styles.previewUnit}> m²/giocatore</span>
              </span>
              {range && (
                // Colore del range: valore runtime dalle costanti calc.
                <span
                  className={styles.rangeChip}
                  style={{ color: range.color, borderColor: range.color }}
                  title={range.notaIt}
                >
                  {range.labelIt}
                </span>
              )}
            </div>
            <DensityScale value={densita} compact />
          </>
        )}
      </div>

      {effettivoOltreDurata && (
        <p className={styles.error}>
          Il tempo effettivo non può superare la durata del blocco.
        </p>
      )}
    </div>
  );
};

export default GameContextFields;
